import React, { useState, useEffect } from 'react';
import './TranslationSimulator.css';

const TranslationSimulator = () => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [activeScenario, setActiveScenario] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('zh');

  // Available languages
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' }
  ];

  // Translation scenarios for different language pairs
  const getScenarios = () => {
    const baseScenarios = [
      {
        id: 1,
        title: '商务会议',
        texts: {
          en: 'Good morning everyone. Today we will discuss the quarterly results and our strategic plan for the next fiscal year.',
          zh: '大家早上好。今天我们将讨论季度业绩以及下一个财年的战略计划。',
          ja: 'おはようございます。本日は四半期の業績と来期の戦略計画について議論します。',
          ko: '안녕하세요. 오늘은 분기 실적과 다음 회계연도의 전략 계획에 대해 논의하겠습니다.',
          fr: 'Bonjour à tous. Aujourd\'hui, nous discuterons des résultats trimestriels et de notre plan stratégique pour le prochain exercice.',
          de: 'Guten Morgen allerseits. Heute werden wir die Quartalsergebnisse und unseren strategischen Plan für das nächste Geschäftsjahr besprechen.',
          es: 'Buenos días a todos. Hoy discutiremos los resultados trimestrales y nuestro plan estratégico para el próximo año fiscal.',
          ru: 'Доброе утро всем. Сегодня мы обсудим квартальные результаты и наш стратегический план на следующий финансовый год.'
        }
      },
      {
        id: 2,
        title: '学术研究',
        texts: {
          en: 'The experimental results demonstrate a significant improvement in efficiency when using the new algorithm compared to traditional methods.',
          zh: '实验结果表明，与传统方法相比，使用新算法时效率有显著提高。',
          ja: '実験結果は、従来の方法と比較して新しいアルゴリズムを使用した場合の効率の顕著な向上を示しています。',
          ko: '실험 결과는 기존 방법과 비교하여 새로운 알고리즘을 사용할 때 효율성이 현저히 향상됨을 보여줍니다.',
          fr: 'Les résultats expérimentaux démontrent une amélioration significative de l\'efficacité lors de l\'utilisation du nouvel algorithme par rapport aux méthodes traditionnelles.',
          de: 'Die experimentellen Ergebnisse zeigen eine signifikante Verbesserung der Effizienz bei Verwendung des neuen Algorithmus im Vergleich zu traditionellen Methoden.',
          es: 'Los resultados experimentales demuestran una mejora significativa en la eficiencia al utilizar el nuevo algoritmo en comparación con los métodos tradicionales.',
          ru: 'Экспериментальные результаты демонстрируют значительное улучшение эффективности при использовании нового алгоритма по сравнению с традиционными методами.'
        }
      },
      {
        id: 3,
        title: '出国旅游',
        texts: {
          en: 'Excuse me, how do I get to the train station? Also, are there any recommended restaurants nearby?',
          zh: '请问去火车站怎么走？还有，附近有什么推荐的餐厅吗？',
          ja: 'すみません、駅へはどう行けばいいですか？それに、近くにおすすめのレストランはありますか？',
          ko: '실례합니다, 기차역에 가려면 어떻게 가야 하나요? 그리고 근처에 추천하는 레스토랑이 있나요？',
          fr: 'Excusez-moi, comment puis-je me rendre à la gare ? Y a-t-il également des restaurants recommandés à proximité ?',
          de: 'Entschuldigung, wie komme ich zum Bahnhof? Gibt es außerdem empfohlene Restaurants in der Nähe?',
          es: 'Disculpe, ¿cómo llego a la estación de tren? Además, ¿hay restaurantes recomendados cerca?',
          ru: 'Извините, как мне добраться до вокзала? И еще, есть ли поблизости рекомендуемые рестораны?'
        }
      },
      {
        id: 4,
        title: '日常对话',
        texts: {
          en: 'I really enjoyed our conversation last night. It was fascinating to learn about your culture and traditions.',
          zh: '我真的很享受昨晚的谈话。了解你的文化和传统非常有趣。',
          ja: '昨夜の会話はとても楽しかったです。あなたの文化や伝統について学ぶのは魅力的でした。',
          ko: '어젯밤 대화가 정말 즐거웠어요. 당신의 문화와 전통에 대해 배우는 것은 매우 흥미로웠습니다.',
          fr: 'J\'ai vraiment apprécié notre conversation hier soir. C\'était fascinant d\'en apprendre davantage sur votre culture et vos traditions.',
          de: 'Ich habe unser Gespräch gestern Abend sehr genossen. Es war faszinierend, mehr über Ihre Kultur und Traditionen zu erfahren.',
          es: 'Realmente disfruté nuestra conversación anoche. Fue fascinante aprender sobre tu cultura y tradiciones.',
          ru: 'Мне очень понравилось наше вчерашнее общение. Было увлекательно узнать о вашей культуре и традициях.'
        }
      }
    ];

    // Map texts to current language pair
    return baseScenarios.map(scenario => ({
      ...scenario,
      sourceText: scenario.texts[sourceLang],
      targetText: scenario.texts[targetLang]
    }));
  };

  const scenarios = getScenarios();
  const currentScenario = scenarios[activeScenario];

  useEffect(() => {
    // Reset translation when language changes
    handleReset();
  }, [sourceLang, targetLang]);

  const handleTranslate = () => {
    if (isTranslating) return;
    
    setIsTranslating(true);
    setShowResult(false);
    
    // Simulate translation process
    setTimeout(() => {
      setTranslatedText(currentScenario.targetText);
      setIsTranslating(false);
      setShowResult(true);
    }, 1500);
  };

  const handleReset = () => {
    setTranslatedText('');
    setShowResult(false);
    setIsTranslating(false);
  };

  const nextScenario = () => {
    setActiveScenario((prev) => (prev + 1) % scenarios.length);
    handleReset();
  };

  const prevScenario = () => {
    setActiveScenario((prev) => (prev - 1 + scenarios.length) % scenarios.length);
    handleReset();
  };

  const swapLanguages = () => {
    const newSource = targetLang;
    const newTarget = sourceLang;
    setSourceLang(newSource);
    setTargetLang(newTarget);
    handleReset();
  };

  const getSourceLanguageName = () => {
    const lang = languages.find(l => l.code === sourceLang);
    return lang ? `${lang.flag} ${lang.name}` : sourceLang;
  };

  const getTargetLanguageName = () => {
    const lang = languages.find(l => l.code === targetLang);
    return lang ? `${lang.flag} ${lang.name}` : targetLang;
  };

  return (
    <div className="translation-simulator-container">
      <div className="simulator-header">
        <h3>深语翻译 - 实时翻译演示</h3>
        <p>体验专业AI翻译的流畅与准确</p>
      </div>
      
      <div className="simulator-controls">
        <button className="nav-button" onClick={prevScenario}>
          ← 上一个场景
        </button>
        <div className="scenario-title">{currentScenario.title}</div>
        <button className="nav-button" onClick={nextScenario}>
          下一个场景 →
        </button>
      </div>
      
      {/* Language Selection */}
      <div className="language-selection">
        <div className="language-selector">
          <select 
            value={sourceLang} 
            onChange={(e) => {
              setSourceLang(e.target.value);
            }}
            className="language-dropdown"
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.flag} {lang.name}
              </option>
            ))}
          </select>
        </div>
        
        <button className="swap-button" onClick={swapLanguages} title="交换语言">
          ⇄
        </button>
        
        <div className="language-selector">
          <select 
            value={targetLang} 
            onChange={(e) => {
              setTargetLang(e.target.value);
            }}
            className="language-dropdown"
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.flag} {lang.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="translation-interface">
        {/* Source text panel */}
        <div className="text-panel source-panel">
          <div className="panel-header">
            <span className="lang-label">{getSourceLanguageName()}</span>
          </div>
          <div className="text-content">
            {currentScenario.sourceText}
          </div>
        </div>
        
        {/* Translation button */}
        <div className="translation-controls">
          <button 
            className={`translate-button ${isTranslating ? 'translating' : ''}`}
            onClick={handleTranslate}
            disabled={isTranslating}
          >
            {isTranslating ? (
              <>
                <span className="spinner"></span>
                翻译中...
              </>
            ) : (
              '深语翻译'
            )}
          </button>
          <div className="arrow-icon">→</div>
        </div>
        
        {/* Target text panel */}
        <div className="text-panel target-panel">
          <div className="panel-header">
            <span className="lang-label">{getTargetLanguageName()}</span>
          </div>
          <div className="text-content">
            {showResult ? (
              <div className="translated-text">
                {translatedText}
              </div>
            ) : (
              <div className="placeholder-text">
                {isTranslating ? '正在翻译...' : '翻译结果将显示在这里'}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Feature highlights */}
      <div className="feature-highlights">
        <div className="feature-card">
          <div className="feature-icon">⚡</div>
          <div className="feature-title">实时翻译</div>
          <div className="feature-desc">毫秒级响应速度</div>
        </div>
        <div className="feature-card">
          <div className="feature-icon">🧠</div>
          <div className="feature-title">AI语境理解</div>
          <div className="feature-desc">上下文精准翻译</div>
        </div>
        <div className="feature-card">
          <div className="feature-icon">🌐</div>
          <div className="feature-title">200+语言</div>
          <div className="feature-desc">全球语言全覆盖</div>
        </div>
      </div>
      
      <div className="simulator-legend">
        <p>选择语言并点击"深语翻译"按钮体验专业AI翻译</p>
        <p>深语翻译 - 以专业AI翻译，定义沟通新境界</p>
      </div>
    </div>
  );
};

export default TranslationSimulator;